import { useCallback, useMemo } from 'react';
import { useDateRange, getDateRange } from './dateRange';
import { useBudget } from './budget';
import { useTravelClass } from './travelClass';
import { useFlightType } from './flightType';
import { useTripDuration } from './tripDuration';
import { useRating } from './rating';
import useFlightStopCount from './flightStopCount';
import { useSorting, transformSortingOption } from './sorting';
import { useFaresApi } from './fares';
import { useOriginDestination } from './locations';
import { useAirlines } from './airlines';
import { useTravelerInterest } from './travelerInterest';
import { isNilOrEmpty } from '../../../helpers/utils';
import { useTrackingChange } from '../../../_integrations/everymundo/tracking';
import { useTrackingChangeDatalayer } from '../../../_integrations/everymundo/tracking/airlines';
import { getAirlines as getAirlinesFn } from '../../../_integrations/everymundo/airlines';
import { getTravelerInterest as getTravelerInterestFn } from '../../../_integrations/everymundo/travelerInterest';
import { getTravelClasses as getTravelClassesFn } from '../../../_integrations/everymundo/breakingDealsTravelClasses';
import { filtersReducer } from './filtersReducer';
import { getCorrectCurrencySettings } from '../../../helpers/utilsPricing';
import { useEffectReducer } from 'use-effect-reducer';

export const OriginDestinationLogic = {
  SAME: 'same',
  DIFFERENT: 'different'
};

function useFilterDisplay({ outputCurrencies, availableFilters, hideBudgetOnMultiCurrency }) {
  return useMemo(() => {
    if (!availableFilters) {
      return [];
    }
    if (hideBudgetOnMultiCurrency && outputCurrencies && outputCurrencies.length !== 1) {
      return availableFilters.filter((f) => f !== 'Budget');
    }
    return availableFilters;
  }, [availableFilters, outputCurrencies, hideBudgetOnMultiCurrency]);
}

function defaultResultsLength(results) {
  if (isNilOrEmpty(results)) {
    return 0;
  }
  return results.length;
}

function getBudget(prePopulation) {
  if (prePopulation?.budget) {
    if (prePopulation?.budget?.maximum || prePopulation?.budget?.minimum) {
      let newBudget = {};
      if (prePopulation?.budget?.maximum) {
        newBudget['maximum'] = prePopulation?.budget?.maximum;
      }
      if (prePopulation?.budget?.minimum) {
        newBudget['minimum'] = prePopulation?.budget?.minimum;
      }
      return newBudget;
    }
  }
  return null;
}

export function useFilters({
  showOrigin = true,
  showDestination = false,
  showFilters = true,
  availableFilters = [],
  showSecondaryFilters = false,
  availableSecondaryFilter = [],
  hideBudgetOnMultiCurrency = true,
  getTravelClassesApi = false,
  filter,
  filter: {
    integrationSettings,
    prePopulation,
    meta,
    pageContext,
    maxItemsAmount,
    nextMonth = false
  },
  getFares,
  paginationSettings,
  faresTransformationLogic,
  initialData,
  initialOrigins,
  initialDestinations,
  showNoResultsPlaceholder = true,
  routeMode = false,
  getResultsLength = defaultResultsLength,
  minInitialResults,
  onLoadMore,
  originDestinationLogic = OriginDestinationLogic.SAME,
  getAirlines = getAirlinesFn,
  getTravelerInterest = getTravelerInterestFn,
  getTravelClasses = getTravelClassesFn,
  enabledhistogram = false,
  hiddenSkeleton = false,
  datalayerTracking = false
}) {
  const {
    outputCurrencies = [],
    priceFormat = {},
    currencySettings = [],
    currencyPricingSettings
  } = prePopulation;

  const displayedFilters = useFilterDisplay({
    outputCurrencies,
    availableFilters,
    hideBudgetOnMultiCurrency
  });

  const displayedSecondaryFilters = useFilterDisplay({
    outputCurrencies,
    availableFilters: availableSecondaryFilter,
    hideBudgetOnMultiCurrency
  });

  const useTracking = () => {
    const trackingFn = datalayerTracking ? useTrackingChangeDatalayer : useTrackingChange;
    return trackingFn();
  };

  const track = useTracking();

  const tracking = useCallback(
    (_, event) => {
      track(event.prevState, event.action, _);
    },
    [track]
  );

  const [state, dispatch] = useEffectReducer(
    filtersReducer,
    {
      originDestinationLogic,
      origin: { value: prePopulation.origin || prePopulation.originCity || '' },
      destination: { value: prePopulation.destination || prePopulation.destinationCity || '' },
      dateRange: getDateRange(prePopulation, maxItemsAmount, nextMonth),
      budget: getBudget(prePopulation),
      travelClass: prePopulation.travelClasses,
      flightStopCount: prePopulation.flightStopCount,
      sorting: transformSortingOption(prePopulation.sorting),
      flightType: prePopulation.flightType,
      tripDuration: prePopulation.tripDuration,
      rating: prePopulation.rating,
      userInteraction: false,
      airlines:
        (prePopulation.origin || prePopulation.originCity) &&
        (prePopulation.destination || prePopulation.destinationCity)
          ? undefined
          : null,
      travelerInterest: {},
      histogramType: null
    },
    {
      tracking
    }
  );

  const trackingDispatch = useCallback(
    (event) => {
      return dispatch({ ...event, category: 'tracking ' });
    },
    [dispatch]
  );

  let enableLocations =
    (showFilters && availableFilters.includes('OriginDestination')) ||
    (showSecondaryFilters &&
      (availableSecondaryFilter.includes('Route') ||
        availableSecondaryFilter.includes('OriginDestination')));

  const locations = useOriginDestination({
    dispatch,
    origin: state.origin,
    destination: state.destination,
    showOrigin: enableLocations && showOrigin,
    showDestination: enableLocations && showDestination,
    filter,
    initialOrigins,
    initialDestinations,
    originDestinationLogic,
    userInteraction: state.userInteraction,
    secondary: showSecondaryFilters && availableSecondaryFilter.includes('Route')
  });

  const airlines = useAirlines({
    dispatch,
    origin: state.origin,
    destination: state.destination,
    isAvailableFilter:
      availableFilters.includes('Airlines') || availableSecondaryFilter.includes('Airlines'),
    getAirlines,
    integrationSettings,
    prePopulation,
    meta,
    pageContext,
    airlinesSetBySetting: prePopulation?.airlines?.airlinesNames
  });

  const travelerInterest = useTravelerInterest({
    dispatch,
    getTravelerInterest,
    integrationSettings,
    prePopulation,
    meta,
    pageContext,
    selectedItems: state.travelerInterest
  });

  const { currencyCode, currencyToDisplay } = getCorrectCurrencySettings(
    priceFormat,
    currencyPricingSettings || currencySettings,
    outputCurrencies[0]
  );

  const dateRange = useDateRange({ dispatch, value: state.dateRange });
  const budget = useBudget({
    value: state.budget,
    dispatch,
    currencyCode: currencyToDisplay || currencyCode
  });
  const travelClass = useTravelClass({
    dispatch,
    value: state.travelClass,
    initValue: prePopulation.travelClasses,
    getTravelClassesApi,
    getTravelClasses,
    integrationSettings,
    prePopulation,
    meta
  });

  const flightStopCount = useFlightStopCount({
    dispatch,
    value: state.flightStopCount,
    initValue: prePopulation.flightStopCount
  });

  const sorting = useSorting({
    dispatch,
    value: state.sorting,
    initValue: transformSortingOption(prePopulation.sorting)
  });
  const flightType = useFlightType({
    dispatch,
    value: state.flightType,
    initValue: prePopulation.flightType
  });
  const tripDuration = useTripDuration({ dispatch, value: state.tripDuration });
  const rating = useRating({ dispatch, value: state.rating });

  const onClear = () => {
    dispatch({
      type: 'CLEAR_ALL',
      payload: {
        travelClass: prePopulation.travelClasses,
        flightStopCount: prePopulation.flightStopCount,
        sorting: transformSortingOption(prePopulation.sorting),
        flightType: prePopulation.flightType,
        tripDuration: prePopulation.tripDuration,
        rating: prePopulation.rating,
        travelerInterest: {}
      }
    });
    // TODO: This shouldn't be necessary
    budget.clear();
  };

  const onClearFiltersSecondary = useCallback(
    (payload) => {
      let newPayload = {
        travelClass: prePopulation.travelClasses,
        flightStopCount: prePopulation.flightStopCount,
        sorting: transformSortingOption(prePopulation.sorting),
        flightType: prePopulation.flightType,
        tripDuration: prePopulation.tripDuration,
        rating: prePopulation.rating,
        ...payload
      };

      dispatch({
        type: 'CLEAR_PARTIAL',
        payload: newPayload
      });
    },
    [
      dispatch,
      prePopulation.flightType,
      prePopulation.sorting,
      prePopulation.flightStopCount,
      prePopulation.travelClasses,
      prePopulation.tripDuration,
      prePopulation.rating
    ]
  );

  const fares = useFaresApi({
    getFares,
    prePopulation,
    selectedOrigin: state.origin,
    selectedDestination: state.destination,
    dateRange: dateRange.value,
    flightStopCount: flightStopCount.value,
    histogramType: state.histogramType,
    travelClasses: travelClass.value,
    flightType: flightType.value,
    tripDuration: tripDuration.value,
    travelerInterest: travelerInterest?.selectedItems,
    rating: rating.value,
    budget: budget.value,
    sorting: sorting.value,
    paginationSettings,
    integrationSettings,
    faresTransformationLogic:
      faresTransformationLogic === 'agreggation' ? 'aggregation' : faresTransformationLogic,
    filter,
    meta,
    initialData,
    showNoResultsPlaceholder,
    getResultsLength,
    minInitialResults,
    routeMode,
    onLoadMore: (info) => {
      onLoadMore(info);
      trackingDispatch({ type: 'LOAD_MORE' });
    },
    selectedAirlines: airlines.isAvailableFilter && !airlines.disabled ? state.airlines : undefined,
    isAirlineFilterAvaliable: airlines.isAvailableFilter,
    isAirlineFilterDisabled: airlines.disabled,
    enabledhistogram,
    hiddenSkeleton,
    pageContext
  });

  const retry = () => {
    if (locations.origin.isError) {
      locations.origin.retry();
    }
    if (locations.destination.isError) {
      locations.destination.retry();
    }
    fares.retry();
  };

  return {
    fares,
    locations,
    retry,
    dateRange,
    budget,
    travelClass,
    flightType,
    tripDuration,
    rating,
    advanced: {
      tripDuration,
      rating
    },
    flightStopCount,
    travelerInterest,
    dispatch,
    onClear,
    onClearFiltersSecondary,
    sorting,
    availableFilters: displayedFilters,
    availableSecondaryFilters: displayedSecondaryFilters,
    tracking: trackingDispatch,
    airlines,
    histogramType: state.histogramType,
    originDestinationLogic
  };
}
